import { AfterViewInit, Component, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { CommonFilterbuilderStaffJanusComponent } from 'src/app/components/common/common-filterbuilder-staff-janus/common-filterbuilder-staff-janus.component';
import { ItemSearchOptionField } from 'src/app/components/filter-list/models/filterClasses';
import { myWorkOptions } from 'src/app/components/common/common-filterbuilder-staff-janus/common-filterbuilder-staff-janus.model';
import { ComponentIds } from 'src/app/models/component-ids';
import { FilterBuilderParam } from 'src/app/models/filter-builder';
import { ColumnOption } from 'src/app/models/grid-settings';
import { ContractorService, DataService, WorkflowContextService, WorkflowService } from 'src/app/services';
import { RegistrationStatus } from 'src/app/models/registration';
import { ContractorsJanusActionComponent } from './contractors-janus-table/cells-janus/contractors-janus-action-cell/contractors-janus-action.component';
import { ToastrService } from 'ngx-toastr';
import { filter, finalize } from 'rxjs/operators';
import { StaffContractorRegistrationDetailsComponent } from './contractor-registration-details/contractor-registration-details.component';
import { ContractorsJanusApplicationComponent } from './contractors-janus-table/contractors-janus-application/contractors-janus-application.component';
import { ContractorsJanusRenewComponent } from './contractors-janus-table/contractors-janus-renew/contractors-janus-renew.component';
import { BehaviorSubject, Subscription } from 'rxjs';
import { TableColumn } from 'src/app/components/common/common-table/common-table.component';
import { ContractorTypeRequirement, Workflow } from 'src/app/models';
import { DataEntityFactory } from 'src/app/services/workflow.service';

type ContractorTab = 'all' | 'types';

const REGISTRATION_STATUS_CONFIG = {
  disableAllowed: ['approved', 'denied', 'inactive'],
  enableAllowed: ['disabled'],
  deleteBlocked: ['deleted'],
  renewBlocked: ['pending', 'deleted']
};

@Component({
  selector: 'app-contractors-janus',
  standalone: false,
  templateUrl: './contractors-janus.component.html',
  styleUrls: ['./contractors-janus.component.scss']
})
export class ContractorsJanusComponent implements AfterViewInit, OnDestroy {
  readonly id = ComponentIds.CONTRACTOR_SEARCH;
  readonly title = 'Contractor Search';
  readonly FILTER_CACHE_KEY = 'contractors-janus-state';

  // Added by Amisha on 07/16/2026 to support the contractor registration tab header filters and exports.
  filterOptions: ItemSearchOptionField[] = [];
  columnOptions: ColumnOption[] = [
    {
      name: 'contractorName',
      label: 'Name',
      checked: true,
      includeInExport: true
    },
    {
      name: 'contractor',
      label: 'Business Name',
      checked: true,
      includeInExport: true
    },
    {
      name: 'type',
      label: 'Contractor Type',
      checked: true,
      includeInExport: true
    },
    {
      name: 'registeredOn',
      label: 'Registration Date',
      checked: true,
      includeInExport: true
    },
    {
      name: 'expiresOn',
      label: 'Expiration Date',
      checked: true,
      includeInExport: true
    },
    { name: 'status', label: 'Status', checked: true, includeInExport: true }
    // { name: 'applicationNumber', label: 'App #', checked: true, includeInExport: true },
    // { name: 'address', label: 'Address', checked: true, includeInExport: true },
    // { name: 'phone', label: 'Phone', checked: true, includeInExport: true },
    // { name: 'email', label: 'Email', checked: true, includeInExport: true },
    // { name: 'submittedBy', label: 'Submitted By', checked: true, includeInExport: true }
  ];
  myWorkOptions: myWorkOptions[] = [
    {
      name: 'Registrations About To Expire',
      templateId: 'expiringRegistrations',
      displayOrder: 1,
      value: '<expiresOn',
      searchText: this.getSevenDaysFromNow()
    }
  ];
  registrations: any[] = [];
  // Added by Amisha on 2026-07-23.
  // Reason: prevent duplicate registration searches during overlapping tab/navigation events
  // and cancel stale requests when the user changes filters, sorting, or paging.
  private hasLoadedRegistrations = false;
  private registrationRequestSignature: string | null = null;
  private registrationRequest?: Subscription;
  private routerEventsSubscription?: Subscription;
  private applicationViewSubscription?: Subscription;
  private refreshedApplicationCloseUrl: string | null = null;
  readonly registrationRequirementColumns$ = new BehaviorSubject<TableColumn[]>(
    []
  );
  private registrationRequirementColumnSignature = '';
  searchQuery = '';
  searchText: ItemSearchOptionField[] = [];
  externalFilters: ItemSearchOptionField[] = [];
  attributeFilters: ItemSearchOptionField[] = [];
  isLoading = false;
  isOverlayOpen = false;
  contractorRegistrationWorkflows: Workflow[] = [];
  contractorWorkflowSearch = '';
  contractorSearchFieldConfig = { label: 'Search', type: 'text', variant: 'outline' };
  isContractorWorkflowLoading = false;
  isStartingContractorApplication = false;
  contractorWorkflowError = '';
  private contractorWorkflowRequest?: Subscription;
  private startApplicationRequest?: Subscription;
  currentPage = 0;
  pageSize = 10;
  totalPages = 0;
  totalRecords = 0;
  sortField = 'registeredOn';
  sortDescending = true;
  useExcelFormat = false;
  modalData: any = {
    open: false,
    modalDescp: '',
    modalSubDescp: '',
    yesNoBtn: false,
    okBtn: false,
    closeBtn: false,
    inTime: 150,
    outTime: 150
  };
  selectedRow: any = null;
  currentAction: 'disable' | 'enable' | 'delete' | null = null;
  @ViewChild(CommonFilterbuilderStaffJanusComponent)
  private filterBuilderComponent?: CommonFilterbuilderStaffJanusComponent;
  private readonly PAGE_SIZE_STORAGE_KEY =
    'wm-common-table-page-size-contractors-janus';
  // Added by Amisha on 07/16/2026 to match the renewals-style registration grid with registration-specific actions.

  statusOptions = [
    { name: 'Pending', value: '0' },
    { name: 'Approved', value: '1' },
    { name: 'Denied', value: '2' },
    { name: 'Expired', value: '3' },
    { name: 'Inactive', value: '4' },
    { name: 'Deleted', value: '5' },
    { name: 'Renewed', value: '6' },
    { name: 'Disabled', value: '7' }
  ];

  // Added by Amisha on 2026-07-29.
  // Reason: build Attribute options from the current table headers instead of maintaining a mismatched static list.
  filterCriteria: FilterBuilderParam[] = [];

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private contractorSvc: ContractorService,
    private dataService: DataService,
    private workflowService: WorkflowService,
    private context: WorkflowContextService,
    private toastr: ToastrService
  ) {
    this.updateFilterCriteriaFromColumns();
  }

  ngOnInit() {
    const storedPageSize = localStorage.getItem(this.PAGE_SIZE_STORAGE_KEY);

    if (storedPageSize) {
      const pageSize = Number(storedPageSize);

      if ([10, 25, 100, 200].includes(pageSize)) {
        this.pageSize = pageSize;
      }
    }

    const currentUrl = this.router.url;
    let previousUrl = currentUrl;
    this.updateOverlayState();

    if (
      currentUrl.endsWith('/contractors') ||
      currentUrl.endsWith('/contractors/')
    ) {
      this.router.navigate(['types'], { relativeTo: this.route });
      return;
    }

    if (this.isRegistrationTabActive()) {
      this.fetchRegistrations();
      this.loadContractorRegistrationWorkflows();
    }

    // Some intermediate activities hide AppView without changing the application URL.
    // Listen to the actual close as well as navigation back to the registrations tab.
    let applicationWasOpen = this.router.url.includes('/contractors/all/application/') ||
      this.context.applicationViewType === 'overlay' ||
      this.context.applicationViewType === 'inline';
    this.applicationViewSubscription = this.context.applicationViewType$.subscribe(viewType => {
      if (viewType !== 'none') {
        applicationWasOpen = true;
        this.refreshedApplicationCloseUrl = null;
        return;
      }
      if (applicationWasOpen && this.router.url.includes('/contractors/all/application/')) {
        applicationWasOpen = false;
        this.refreshedApplicationCloseUrl = this.router.url;
        this.registrationRequest?.unsubscribe();
        this.fetchRegistrations();
      }
    });

    this.routerEventsSubscription = this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        const registrationTabActive = this.isRegistrationTabActive();
        const enteredRegistrationTab =
          registrationTabActive && !this.isRegistrationTabUrl(previousUrl);
        const returnedFromRegistrationApplication =
          this.isRegistrationApplicationUrl(previousUrl) &&
          registrationTabActive &&
          !this.isRegistrationApplicationUrl(this.router.url);

        const needsCloseRefresh = returnedFromRegistrationApplication &&
          this.refreshedApplicationCloseUrl !== previousUrl;
        this.refreshedApplicationCloseUrl = null;
        this.updateOverlayState();
        if (needsCloseRefresh) {
          // A search started before processing finished may contain the old status.
          this.registrationRequest?.unsubscribe();
        }
        if (
          enteredRegistrationTab ||
          needsCloseRefresh ||
          (registrationTabActive && !this.hasLoadedRegistrations)
        ) {
          this.fetchRegistrations();
          if (enteredRegistrationTab || returnedFromRegistrationApplication) {
            this.loadContractorRegistrationWorkflows();
          }
        }

        previousUrl = this.router.url;
      });
  }

  isTabActive(tabName: ContractorTab): boolean {
    const currentUrl = this.router.url;

    if (tabName === 'all') {
      return (
        currentUrl.includes('/all') ||
        currentUrl.includes('(all//') ||
        currentUrl.includes('/(all//')
      );
    }

    return (
      currentUrl.includes('/types') ||
      currentUrl.includes('(types//') ||
      currentUrl.includes('/(types//') ||
      currentUrl.endsWith('/contractors') ||
      currentUrl.endsWith('/contractors/')
    );
  }

  isRegistrationTabActive(): boolean {
    return this.isTabActive('all');
  }

  private isRegistrationTabUrl(url: string): boolean {
    return url.includes('/contractors/all');
  }

  private isRegistrationApplicationUrl(url: string): boolean {
    if (!this.isRegistrationTabUrl(url)) {
      return false;
    }

    const view = this.router.parseUrl(url).queryParams['view'];
    return url.includes('/contractors/all/application/') ||
      view === 'application' || view === 'renew' || view === 'details';
  }

  onSearch(value: ItemSearchOptionField[]) {
    this.searchText = this.cloneFilters(value);
    this.searchQuery = this.getSearchQuery(this.searchText);
    this.currentPage = 0;
    this.fetchRegistrations();
  }

  onAttribute(_value: string) {}

  onFilterChanged(value: ItemSearchOptionField[]) {
    this.attributeFilters = this.cloneFilters(value);
    this.currentPage = 0;
    this.fetchRegistrations();
  }

  onExternalFiltersChanged(value: ItemSearchOptionField[]) {
    this.externalFilters = this.cloneFilters(value || []);
    this.currentPage = 0;
    this.fetchRegistrations();
  }

  exportExcel() {
    this.useExcelFormat = true;
    this.export();
  }

  exportCsv() {
    this.useExcelFormat = false;
    this.export();
  }

  ensureRegistrationData() {
    if (!this.hasLoadedRegistrations) {
      this.fetchRegistrations();
    }
  }

  onRegistrationPageChange(page: number) {
    this.currentPage = page;
    this.fetchRegistrations();
  }

  onRegistrationSortChanged(event: {
    sortField: string | null;
    sortDescending: boolean | null;
  }) {
    this.sortField = event.sortField || 'registeredOn';
    this.sortDescending =
      event.sortDescending == null ? true : event.sortDescending;
    this.currentPage = 0;
    this.fetchRegistrations();
  }

  onRegistrationRowsPerPageChange(size: number) {
    const pageSize = Number(size) || 10;
    localStorage.setItem(this.PAGE_SIZE_STORAGE_KEY, String(pageSize));
    this.pageSize = pageSize;
    this.currentPage = 0;
    this.fetchRegistrations();
  }

  onRegistrationColumnsChanged(displayedColumns: string[]): void {
    const displayedColumnSet = new Set(displayedColumns);

    this.columnOptions = this.columnOptions.map(option => ({
      ...option,
      checked: displayedColumnSet.has(option.name)
    }));
    this.updateFilterCriteriaFromColumns();
  }

  onRegistrationExportColumnsChanged(exportColumns: string[]): void {
    const exportColumnSet = new Set(exportColumns);

    this.columnOptions = this.columnOptions.map(option => ({
      ...option,
      includeInExport: exportColumnSet.has(option.name)
    }));
  }

  openActionModal(row: any, action: 'disable' | 'enable' | 'delete') {
    this.selectedRow = row;
    this.currentAction = action;

    if (action === 'disable') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Disable Registration',
        modalSubDescp: 'Are you sure you want to disable this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }

    if (action === 'enable') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Enable Registration',
        modalSubDescp:
          'This registration will be restored to a status determined by its expiration date and/or requirements.\n\nAre you sure you want to enable this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }

    if (action === 'delete') {
      this.modalData = {
        ...this.modalData,
        open: true,
        modalDescp: 'Delete Registration',
        modalSubDescp: 'Are you sure you want to delete this registration?',
        yesNoBtn: true,
        okBtn: false
      };
    }
  }

  private fetchRegistrations() {
    // Added by Amisha on 07/16/2026 to load contractor registrations from SearchContractorRegistrations for the new tab.
    if (!this.isRegistrationTabActive() || !this.context.client?.id) {
      return;
    }

    const filters = this.buildFilters();
    // Added by Amisha on 07/17/2026 to match the legacy contractor list paging.
    // The contractor registrations API expects a zero-based page index, same as renewals/legacy datatable.
    const pageNumber = this.currentPage;
    const requestSignature = JSON.stringify({
      clientId: this.context.client.id,
      sortField: this.sortField,
      sortDescending: this.sortDescending,
      filters,
      pageNumber,
      pageSize: this.pageSize
    });

    if (
      this.registrationRequestSignature === requestSignature &&
      !this.registrationRequest?.closed
    ) {
      return;
    }

    // Added by Amisha on 2026-07-23.
    // Reason: a changed query supersedes an unfinished one, whose late response
    // must not overwrite the latest filter, sort, or paging result.
    this.registrationRequest?.unsubscribe();
    this.registrationRequestSignature = requestSignature;
    this.isLoading = true;
    this.filterOptions = filters;

    this.registrationRequest = this.contractorSvc
      .searchContractorRegistrations(
        this.context.client.id,
        this.sortField,
        this.sortDescending,
        filters,
        pageNumber,
        this.pageSize
      )
      .pipe(
        finalize(() => {
          if (this.registrationRequestSignature === requestSignature) {
            this.registrationRequestSignature = null;
            this.isLoading = false;
          }
        })
      )
      .subscribe({
        next: result => {
          this.setRegistrationRequirementColumns(result?.requirements || []);
          this.registrations = (
            result?.contractorRegistrations || []
          ).map(registration => this.mapRegistrationRow(registration));
          this.totalPages = result?.pageCount || 0;
          this.totalRecords = result?.recordCount || 0;
          this.hasLoadedRegistrations = true;
        },
        error: error => {
          console.error(error);
          this.toastr.error('Unable to load contractor registrations.');
          this.registrations = [];
          this.hasLoadedRegistrations = false;
          this.setRegistrationRequirementColumns([]);
          this.totalPages = 0;
        }
      });
  }

  private mapRegistrationRow(registration: any) {
    // Added by Amisha on 07/16/2026 to map API response fields into the simplified registration table shape.
    const contractor = registration?.contractor || {};
    const contactName = `${contractor?.contactFirstName ||
      ''} ${contractor?.contactLastName || ''}`.trim();
    const address = [
      contractor?.address1,
      contractor?.address2,
      contractor?.city,
      contractor?.state,
      contractor?.zip
    ]
      .filter(Boolean)
      .join(', ');
    const customData = this.mapRegistrationRequirementValues(registration);

    return {
      id: registration?.id,

      createdApplicationId: registration?.registerApplicationId,

      contractorId: registration?.contractorID,

      contractorTypeId:
        registration?.contractorTypeID ||
        registration?.contractorTypeId ||
        registration?.type?.id,

      contractorName: contactName || 'N/A',

      contractor: contractor?.businessName || 'N/A',

      type:
        registration?.type?.name || registration?.contractorType?.name || 'N/A',

      registeredOn: registration?.registeredOnString || 'N/A',

      expiresOn: registration?.expiresOnString || 'N/A',

      status:
        registration?.status != null
          ? RegistrationStatus[registration.status]
          : '',

      disabled:
        String(RegistrationStatus[registration?.status] || '').toLowerCase() ===
        'disabled',
      ...customData
    };
  }

  private setRegistrationRequirementColumns(
    requirements: ContractorTypeRequirement[]
  ): void {
    const uniqueRequirements = (requirements || []).filter(
      (requirement, index, all) =>
        !!requirement?.templateCode &&
        all.findIndex(
          item => item?.templateCode === requirement.templateCode
        ) === index
    );
    const signature = uniqueRequirements
      .map(requirement => `${requirement.templateCode}:${requirement.label}`)
      .join('|');

    if (signature === this.registrationRequirementColumnSignature) {
      return;
    }

    const previousDynamicKeys = new Set(
      this.registrationRequirementColumns$.value.map(column => column.key)
    );
    const existingOptions = new Map(
      this.columnOptions.map(option => [option.name, option])
    );

    this.registrationRequirementColumnSignature = signature;
    this.registrationRequirementColumns$.next(
      uniqueRequirements.map(requirement => ({
        key: requirement.templateCode,
        label: requirement.label || requirement.templateCode,
        width: '180px',
        headerType: 'text',
        cellType: 'text',
        hidden: true,
        showExportOption: true
      }))
    );

    const dynamicKeys = new Set(
      uniqueRequirements.map(requirement => requirement.templateCode)
    );
    this.columnOptions = [
      ...this.columnOptions.filter(
        option =>
          !previousDynamicKeys.has(option.name) && !dynamicKeys.has(option.name)
      ),
      ...uniqueRequirements.map(requirement => {
        const existingOption = existingOptions.get(requirement.templateCode);

        return {
          name: requirement.templateCode,
          label: requirement.label || requirement.templateCode,
          checked: existingOption?.checked ?? false,
          includeInExport: existingOption?.includeInExport ?? true
        };
      })
    ];
    this.updateFilterCriteriaFromColumns();
  }

  // Added by Amisha on 2026-07-29.
  // Reason: mirror visible contractor header labels and left-to-right order while preserving API filter metadata.
  private updateFilterCriteriaFromColumns(): void {
    const configMap: Record<string, Partial<FilterBuilderParam>> = {
      registeredOn: {
        inputType: 'date',
        types: ['is', 'range']
      },
      expiresOn: {
        inputType: 'date',
        types: ['is', 'range']
      },
      status: {
        types: ['is', 'in'],
        options: this.statusOptions
      }
    };

    this.filterCriteria = this.columnOptions
      .filter(column => column.checked)
      .map(column => ({
        id: column.name,
        name: column.label,
        types: configMap[column.name]?.types || ['contains', 'is'],
        inputType: configMap[column.name]?.inputType,
        options: configMap[column.name]?.options
      }));
  }

  private mapRegistrationRequirementValues(
    registration: any
  ): Record<string, string> {
    const values: Record<string, string> = Object.fromEntries(
      this.registrationRequirementColumns$.value.map(column => [column.key, ''])
    );

    for (const requirement of registration?.type?.requirements || []) {
      const customField =
        (registration?.customFields || []).find(
          (field: any) => field.templateCode === requirement.templateCode
        ) ||
        (registration?.contractor?.customFields || []).find(
          (field: any) => field.templateCode === requirement.templateCode
        );

      values[requirement.templateCode] = this.formatCustomField(customField);
    }

    return values;
  }

  private formatCustomField(customField: any): string {
    if (!customField) {
      return 'N/A';
    }

    const dataEntityType =
      customField.dataEntityType ||
      customField.dataEntityDefinition?.dataEntityTypeCode;

    if (!dataEntityType) {
      return customField.fieldValue == null || customField.fieldValue === ''
        ? 'N/A'
        : String(customField.fieldValue);
    }

    try {
      const dataEntity = DataEntityFactory.createDataEntity(dataEntityType, {
        value: customField.fieldValue,
        label: customField.label,
        templateCode: customField.templateCode
      });

      return dataEntity.getFormattedValue() || 'N/A';
    } catch {
      return customField.fieldValue == null || customField.fieldValue === ''
        ? 'N/A'
        : String(customField.fieldValue);
    }
  }

  private buildFilters(): ItemSearchOptionField[] {
    const filters = this.cloneFilters([
      ...this.searchText,
      ...this.externalFilters,
      ...this.attributeFilters
    ]);

    return filters
      .filter(filter => {
        const option: any = filter?.options?.[0];
        if (!option) {
          return false;
        }

        return !!(
          option.searchText ||
          option.filterText ||
          option.customValue?.start ||
          option.customValue?.end
        );
      })
      .map(filter => {
        const option: any = filter?.options?.[0];

        if (option?.searchOptionMethod === 'simpleSearch') {
          return filter;
        }

        if (option?.filterText && !option?.searchText) {
          option.searchText = option.filterText;
          option.filterText = '';
        }

        return filter;
      });
  }

  private export() {
    if (!this.context.client?.id) {
      return;
    }

    const exportColumns = this.columnOptions
      .filter(column => column.includeInExport)
      .map(column => column.name);

    this.contractorSvc
      .exportContractorRegistrations(
        this.context.client.id,
        this.useExcelFormat,
        exportColumns,
        this.sortField,
        this.sortDescending,
        this.buildFilters(),
        this.currentPage,
        this.pageSize
      )
      .subscribe({
        next: data => {
          const blob = new Blob([data], {
            type: this.useExcelFormat
              ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
              : 'text/csv'
          });
          const link = document.createElement('a');
          link.href = window.URL.createObjectURL(blob);
          link.download = this.useExcelFormat
            ? `contractor-registrations-export-${new Date().getTime()}.xlsx`
            : `contractor-registrations-export-${new Date().getTime()}.csv`;
          link.click();
        },
        error: error => {
          console.error(error);
          this.toastr.error('Problem while downloading the file.');
        }
      });
  }

  private cloneFilters(
    filters: ItemSearchOptionField[] = []
  ): ItemSearchOptionField[] {
    return JSON.parse(JSON.stringify(filters || []));
  }

  get displayedContractorWorkflows(): Workflow[] {
    const search = this.contractorWorkflowSearch.trim().toLowerCase();
    return this.contractorRegistrationWorkflows.filter(workflow =>
      !search || (workflow.version?.name || workflow.name || '').toLowerCase().includes(search)
    );
  }
  loadContractorRegistrationWorkflows(): void {
    const clientId = this.route.parent?.parent?.snapshot.paramMap.get('clientId') || this.context.client?.id;
    if (!clientId) {
      this.contractorWorkflowError = 'Unable to determine the jurisdiction.';
      return;
    }
    this.contractorWorkflowRequest?.unsubscribe();
    this.contractorWorkflowError = '';
    this.isContractorWorkflowLoading = true;
    this.contractorWorkflowRequest = this.dataService
      .getContractorRegistrationWorkflows(clientId, this.context.user?.id || null)
      .pipe(finalize(() => this.isContractorWorkflowLoading = false))
      .subscribe({
        next: workflows => {
          this.contractorRegistrationWorkflows = (workflows || []).filter(
            workflow => workflow.isEnabled && workflow.isPublished
          );
        },
        error: () => {
          this.contractorRegistrationWorkflows = [];
          this.contractorWorkflowError = 'Unable to load contractor registrations.';
        }
      });
  }
  startContractorRegistration(workflowId: string): void {
    if (this.isStartingContractorApplication || !workflowId) return;
    this.isStartingContractorApplication = true;
    this.contractorWorkflowError = '';
    this.startApplicationRequest = this.workflowService
      .startApplication(workflowId, false)
      .subscribe({
        next: response => {
          if (!response?.applicationId) {
            this.isStartingContractorApplication = false;
            this.contractorWorkflowError = 'Unable to start contractor registration.';
            return;
          }
          this.router.navigate(['all', 'application', response.applicationId], {
            relativeTo: this.route,
            state: { startingActivityId: '1', useNewLayout: this.context.useNewLayout }
          }).then(navigated => {
            if (navigated && this.router.url.includes('/contractors/all/application/' + response.applicationId)) {
              this.context.applicationViewType$.emit('overlay');
            } else {
              this.contractorWorkflowError = 'Unable to open contractor registration.';
            }
          }).catch(() => {
            this.contractorWorkflowError = 'Unable to open contractor registration.';
          }).finally(() => {
            this.isStartingContractorApplication = false;
          });
        },
        error: () => {
          this.isStartingContractorApplication = false;
          this.contractorWorkflowError = 'Unable to start contractor registration.';
        }
      });
  }
  ngAfterViewInit(): void {
    this.syncSearchQueryToFilterBuilder();
  }

  ngOnDestroy(): void {
    // Added by Amisha on 2026-07-23.
    // Reason: prevent destroyed contractor tabs from retaining router or HTTP subscriptions.
    this.registrationRequest?.unsubscribe();
    this.contractorWorkflowRequest?.unsubscribe();
    this.startApplicationRequest?.unsubscribe();
    this.routerEventsSubscription?.unsubscribe();
    this.applicationViewSubscription?.unsubscribe();
  }

  private getSearchQuery(searchText: ItemSearchOptionField[]): string {
    const firstOption = searchText?.[0]?.options?.[0];

    return firstOption?.filterText || firstOption?.searchText || '';
  }

  private getSevenDaysFromNow(): string {
    const date = new Date();
    date.setDate(date.getDate() + 7);

    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
  }

  private syncSearchQueryToFilterBuilder(): void {
    if (!this.filterBuilderComponent) {
      return;
    }

    this.filterBuilderComponent.searchQuery = this.searchQuery || '';
  }

  private updateOverlayState(): void {
    this.isOverlayOpen = this.router.url.includes('(overlay:');
  }

  confirmAction(): void {
    if (!this.selectedRow?.id || !this.currentAction) {
      return;
    }

    const row = this.selectedRow;
    const action = this.currentAction;

    this.modalData = {
      ...this.modalData,
      open: false
    };

    if (action === 'enable') {
      this.contractorSvc.reEnableContractorRegistration(row.id).subscribe({
        next: (res: boolean) => {
          if (res) {
            row.disabled = false;
            this.fetchRegistrations();
            this.openSuccessModal(action);
          }
        },
        error: err => console.error(err)
      });
    }

    if (action === 'disable') {
      this.contractorSvc.disableContractorRegistration(row.id).subscribe({
        next: (res: boolean) => {
          if (res) {
            row.status = 'Disabled';
            row.disabled = true;
            this.registrations = [...this.registrations];
            this.openSuccessModal(action);
          }
        },
        error: err => console.error(err)
      });
    }

    if (action === 'delete') {
      this.contractorSvc.deleteContractorRegistration(row.id).subscribe({
        next: () => {
          this.registrations = this.registrations.filter(r => r.id !== row.id);
          this.loadContractorRegistrationWorkflows();
          this.openSuccessModal(action);
        },
        error: err => console.error(err)
      });
    }

    this.currentAction = null;
  }

  getSuccessMessage(action: string): string {
    switch (action) {
      case 'disable':
        return 'Your registration has been disabled successfully.';
      case 'enable':
        return 'Your registration has been enabled successfully.';
      case 'delete':
        return 'Your registration has been deleted successfully.';
      default:
        return 'Action completed successfully.';
    }
  }

  openSuccessModal(action: string): void {
    this.modalData = {
      ...this.modalData,
      open: true,
      modalDescp: 'Contractors',
      modalSubDescp: this.getSuccessMessage(action),
      yesNoBtn: false,
      okBtn: true,
      closeBtn: false
    };
  }

  closeModal(): void {
    this.modalData = {
      ...this.modalData,
      open: false
    };

    this.currentAction = null;
    this.selectedRow = null;
    this.registrations = [...this.registrations];
  }

  cancelAction(): void {
    this.modalData = {
      ...this.modalData,
      open: false
    };

    this.currentAction = null;
  }

  public showWorkInProgress() {
    this.toastr.warning('Work in progress.');
  }

  private canRenew(item: any): boolean {
    return !REGISTRATION_STATUS_CONFIG.renewBlocked.includes(
      String(item?.status || '').toLowerCase()
    );
  }

  private canDelete(item: any): boolean {
    return !REGISTRATION_STATUS_CONFIG.deleteBlocked.includes(
      String(item?.status || '').toLowerCase()
    );
  }

  private canDisable(item: any): boolean {
    return REGISTRATION_STATUS_CONFIG.disableAllowed.includes(
      String(item?.status || '').toLowerCase()
    );
  }

  private canEnable(item: any): boolean {
    return REGISTRATION_STATUS_CONFIG.enableAllowed.includes(
      String(item?.status || '').toLowerCase()
    );
  }

  public showWarning(message: string): void {
    this.toastr.warning(message);
  }
}
